import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import ExperimentFormModal from '../components/ExperimentFormModal';
import ExperimentDetailsModal from '../components/ExperimentDetailsModal';
import { Plus, Search, Trash2, ArrowLeft, Folder } from 'lucide-react';

const ExperimentTracking = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [experiments, setExperiments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedExperiment, setSelectedExperiment] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  const fetchProjectDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('experiments')
        .select('*')
        .eq('id', projectId)
        .single();

      if (error) throw error;
      setProject(data);
    } catch (error) {
      console.error('Error fetching project details:', error);
    }
  };

  const fetchExperiments = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('experiments')
        .select('*')
        .eq('project_id', projectId)
        .eq('type', 'experiment')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setExperiments(data || []);
    } catch (error) {
      console.error('Error fetching experiments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId) {
      navigate('/experiment-tracking');
      return;
    }

    fetchProjectDetails();
    fetchExperiments();

    // Set up real-time subscription
    const subscription = supabase
      .channel(`experiments_project_${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'experiments',
          filter: `project_id=eq.${projectId}`,
        },
        (payload) => {
          // Only process if it's an experiment type
          if (payload.new && payload.new.type !== 'experiment' && payload.eventType !== 'DELETE') return;

          if (payload.eventType === 'INSERT') {
            setExperiments((prev) => {
              if (prev.some(exp => exp.id === payload.new.id)) return prev;
              return [payload.new, ...prev];
            });
          } else if (payload.eventType === 'UPDATE') {
            setExperiments((prev) =>
              prev.map((exp) => (exp.id === payload.new.id ? payload.new : exp))
            );
          } else if (payload.eventType === 'DELETE') {
            setExperiments((prev) => prev.filter((exp) => exp.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [projectId, navigate]);

  // Handle opening specific experiment from notification
  const location = useLocation();
  useEffect(() => {
    const experimentToOpenId = location.state?.openExperimentId;
    if (experimentToOpenId && experiments.length > 0) {
      const experimentToOpen = experiments.find(e => e.id === experimentToOpenId);
      if (experimentToOpen) {
        setSelectedExperiment(experimentToOpen);
        setDetailsModalOpen(true);
        // Clear state
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, experiments, navigate]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this experiment?')) {
      return;
    }

    try {
      // Optimistic update
      setExperiments((prev) => prev.filter((exp) => exp.id !== id));

      const { error } = await supabase.from('experiments').delete().eq('id', id);

      if (error) {
        // Revert if error
        fetchExperiments();
        throw error;
      }
    } catch (error) {
      console.error('Error deleting experiment:', error);
      alert('Error deleting experiment: ' + error.message);
    }
  };

  const filteredExperiments = experiments.filter((exp) => {
    const query = searchQuery.toLowerCase();
    return (
      exp.title?.toLowerCase().includes(query) ||
      exp.category?.toLowerCase().includes(query) ||
      exp.hypothesis?.toLowerCase().includes(query) ||
      exp.lesson_learned?.toLowerCase().includes(query)
    );
  });

  const totalExperiments = experiments.length;
  const successCount = experiments.filter((exp) => exp.status === 'Success').length;
  const successRate = totalExperiments > 0 ? Math.round((successCount / totalExperiments) * 100) : 0;
  const activeTests = experiments.filter(
    (exp) => exp.status === 'In Progress' || exp.status === 'Pending'
  ).length;

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    });
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    return `${dateStr} ${timeStr}`;
  };

  return (
    <div className="h-full flex flex-col bg-black relative overflow-hidden">
      {/* Coming Soon Overlay */}
      <div className="coming-soon-overlay">
        <div className="coming-soon-text">Coming Soon</div>
      </div>
      {/* Header */}
      <div className="bg-black border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4 mb-4">
            <Link
              to="/experiment-tracking"
              className="p-2 hover:bg-white/5 rounded-lg text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft size={20} />
            </Link>
            <div className="flex items-center gap-2 text-white">
              <Folder size={18} />
              <span className="text-sm font-medium">{project?.title || project?.name || 'Loading project...'}</span>
            </div>
          </div>

          <div className="flex justify-between items-center gap-2">
            <div className="flex-1 min-w-0">
              <h1 className="text-lg lg:text-xl font-bold text-white mb-0.5">
                Experiments Dashboard
              </h1>
              <p className="text-gray-400 text-xs lg:text-sm">
                {project?.description || 'Document, analyze, and learn from every test.'}
              </p>
            </div>
            <button
              onClick={() => {
                setSelectedExperiment(null);
                setIsModalOpen(true);
              }}
              className="flex items-center gap-1.5 bg-white text-black px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-all shadow-[0_0_15px_rgba(255,255,255,0.1)] text-sm lg:text-base flex-shrink-0 font-bold"
            >
              <Plus size={16} className="lg:hidden" />
              <Plus size={18} className="hidden lg:block" />
              <span className="lg:hidden">New</span>
              <span className="hidden lg:inline">New Experiment</span>
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin">
        <div className="max-w-7xl mx-auto px-3 lg:px-4 py-3 lg:py-4">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 lg:gap-3 mb-3 lg:mb-4">
            <div className="bg-black rounded-lg p-3 lg:p-4 border border-white/10">
              <h3 className="text-[10px] lg:text-xs font-medium text-gray-400 mb-0.5 lg:mb-1">Total Experiments</h3>
              <p className="text-xl lg:text-2xl font-bold text-white">{totalExperiments}</p>
            </div>

            <div className="bg-black rounded-lg p-3 lg:p-4 border border-white/10">
              <h3 className="text-[10px] lg:text-xs font-medium text-gray-400 mb-0.5 lg:mb-1">Success Rate</h3>
              <p className="text-xl lg:text-2xl font-bold text-white">{successRate}%</p>
            </div>

            <div className="bg-black rounded-lg p-3 lg:p-4 border border-white/10 col-span-2 md:col-span-1">
              <h3 className="text-[10px] lg:text-xs font-medium text-gray-400 mb-0.5 lg:mb-1">Active Tests</h3>
              <p className="text-xl lg:text-2xl font-bold text-white">{activeTests}</p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative mb-3 lg:mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search experiments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full max-w-sm pl-9 pr-3 lg:pr-4 py-1.5 lg:py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/20 text-sm text-white placeholder-gray-500 transition-all"
            />
          </div>

          {/* Experiments List */}
          <div className="space-y-2 lg:space-y-3 pb-4">
            {loading ? (
              <div className="bg-black rounded-lg p-4 lg:p-6 text-center border border-white/10">
                <p className="text-gray-400 text-sm">Loading experiments...</p>
              </div>
            ) : filteredExperiments.length === 0 ? (
              <div className="bg-black rounded-lg p-4 lg:p-6 text-center border border-white/10">
                <p className="text-gray-400 text-sm">
                  {searchQuery
                    ? 'No experiments found matching your search.'
                    : 'No experiments yet in this project. Create your first one!'}
                </p>
              </div>
            ) : (
              filteredExperiments.map((experiment) => (
                <div
                  key={experiment.id}
                  className="bg-black rounded-lg border border-white/10 p-3 lg:p-4 hover:border-white/20 transition-all cursor-pointer"
                  onClick={() => {
                    setSelectedExperiment(experiment);
                    setDetailsModalOpen(true);
                  }}
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="text-sm lg:text-base font-semibold text-white">
                          {experiment.title}
                        </h3>
                        {experiment.linked_test && (
                          <span className="text-[10px] lg:text-xs text-gray-400">
                            @ <span className="text-white font-medium">{experiment.linked_test}</span>
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 lg:gap-3 text-[10px] lg:text-xs text-gray-400 mb-1 flex-wrap">
                        <span className="truncate">{formatDateTime(experiment.created_at)}</span>
                        <span className="hidden lg:inline">•</span>
                        <span className="text-white font-medium">
                          {experiment.category}
                        </span>
                      </div>
                      <div className="text-[10px] lg:text-xs text-gray-400 truncate">
                        Created by <span className="text-gray-300 font-medium">{experiment.creator_name}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 lg:gap-2 flex-shrink-0">
                      <span
                        className={`px-2 py-0.5 lg:py-1 rounded text-[9px] lg:text-[10px] uppercase font-bold tracking-tight ${experiment.status === 'Success'
                          ? 'border border-white text-white bg-white/10'
                          : experiment.status === 'Failure'
                            ? 'border border-gray-600 text-gray-500 bg-black italic'
                            : 'border border-gray-400 text-gray-400 bg-white/5'
                          }`}
                      >
                        {experiment.status}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(experiment.id);
                        }}
                        className="p-1 lg:p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                      >
                        <Trash2 size={14} className="lg:block hidden" />
                        <Trash2 size={12} className="lg:hidden" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <ExperimentFormModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={() => fetchExperiments()}
          projectId={projectId}
          experiment={selectedExperiment}
        />
      )}

      {detailsModalOpen && selectedExperiment && (
        <ExperimentDetailsModal
          experiment={selectedExperiment}
          onClose={() => {
            setDetailsModalOpen(false);
            setSelectedExperiment(null);
          }}
          onEdit={() => {
            setDetailsModalOpen(false);
            setIsModalOpen(true);
          }}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default ExperimentTracking;
